import React, { ReactElement, useEffect } from 'react';
import { LayoutScreenConfig, MedicareAccessScreenName, ScreenType } from '../../config/FlowTypes';
import { store } from '../../store/Store';
import { Analytics } from '../../util/Analytics';
import { isMedicareOriginal } from '../../util/MedicareAccess';
import { ScreenWrapper } from '../styles/Containers';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout } from './layouts/PresentationLayout';

type ResultsLayoutConfig = {
  title?: string;
  summaryBoxHeading?: string;
  summaryBoxBody?: string;
  body: string[];
  primaryButtonTitle: string;
  textAnimationEnabled?: boolean;
};

export type ResultsScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.Results,
  ResultsLayoutConfig,
  ScreenNames
>;

type ResultsScreenProps<ScreenNames extends string> = NavigationProps<
  ResultsScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ResultsScreen = <ScreenNames extends string>(
  props: ResultsScreenProps<ScreenNames>
): ReactElement => {
  const { layout, navigation } = props;

  useEffect(function fireInsuranceOutcomeWhenNotOriginalMedicare() {
    const state = store.getState();
    const userData = state.userData as Partial<
      Record<MedicareAccessScreenName.MedicareAccessDirectHealthCover, string[]>
    >;
    const directHealthCover =
      userData[MedicareAccessScreenName.MedicareAccessDirectHealthCover]?.[0];
    // Out-of-state Original Medicare users can't join ACCESS, so they count as unsuccessful too.
    if (directHealthCover !== undefined && !isMedicareOriginal(state)) {
      Analytics.track({ event: Analytics.Event.o_insurance_outcome, outcome: 'unsuccessful' });
    }
  }, []);

  return (
    <ScreenWrapper>
      <PresentationLayout
        title={layout.title}
        content={[
          {
            type: 'coloredBox',
            heading: layout.summaryBoxHeading,
            body: layout.summaryBoxBody,
            textAlign: 'center',
          },
          ...layout.body.map((content) => ({ type: 'text' as const, content })),
        ]}
        onPressPrimaryButton={() => navigation.next(props)}
        primaryButtonTitle={layout.primaryButtonTitle}
        textAnimationEnabled={layout.textAnimationEnabled}
      />
    </ScreenWrapper>
  );
};
